// src/plugins/msalConfig.js
//
// Vue3-Migration Phase 1b: Ablösung von `vue-msal` (Vue2-only, seit 2020 unmaintained)
// durch `@azure/msal-browser` direkt + ein dünnes Vue-3-Plugin.
//
// Das Plugin stellt die App-seitig tatsächlich genutzte MSAL-Oberfläche bereit
// (per grep über src/ ermittelt):
//   - App.vue:          this.msal.isAuthenticated, this.msal.user.userName, this.$msal.signIn()
//   - SidebarMenu.vue:  this.$msal?.data?.user?.name, (auskommentiert) this.$msal.signOut()
// Sowohl `$msal` als auch `msal` (früher via vue-msal globalMixin) zeigen auf DASSELBE
// reaktive Objekt, das genau diese Properties/Methoden anbietet.
//
// KRITISCH — Dev-Bypass: Auf localhost im Dev-Build (bzw. wenn Playwright
// sessionStorage['DEV_MSAL_BYPASS']='1' seedet) darf MSAL NICHT initialisieren/redirecten.
// Die App überspringt den Login dann in App.vue#created. Wir spiegeln dieselbe
// Bypass-Erkennung hier, damit `@azure/msal-browser` gar nicht erst gebaut/initialisiert
// wird und keinerlei Entra-Redirect auslöst.

import { reactive } from 'vue'
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser'

// Reaktiver Zustand, den die Komponenten lesen (ersetzt den früheren vue-msal `msal`-Mixin-State).
const msalState = reactive({
  isAuthenticated: false,
  // vue-msal-kompatible Form: user.userName (App.vue) + data.user.name (SidebarMenu.vue)
  user: null,
  data: { user: null }
})

let msalApp = null
// Beim Login wird direkt der Scope der eigenen API angefordert, damit nach der
// Rueckkehr sofort ein verwendbares Access-Token im Cache liegt.
//
// WICHTIG: Entra stellt pro Token-Request nur Scopes EINER Ressource aus. Der
// frueher hier stehende Graph-Scope `User.Read` haette zusammen mit dem
// API-Scope zwei Ressourcen gemischt — das Ergebnis waere ein Token fuer Graph
// gewesen, und der erste API-Request nach dem Login haette mangels
// API-Token-Cache 401 bekommen. Graph wird von der App nirgends aufgerufen
// (`grep -rn "graph.microsoft" src/` ist leer), der Scope entfaellt daher.
// `offline_access` liefert das Refresh-Token fuer die stille Erneuerung.
const loginRequest = { scopes: ['openid', 'profile', 'offline_access'] }

/**
 * Scope der eigenen API. Das Backend validiert Tokens mit dieser Audience gegen
 * die JWKS von Entra und entnimmt die Identitaet den verifizierten Claims —
 * frueher lag hier ein statischer, im Bundle ausgelieferter Token.
 */
const apiScope = () =>
  (typeof config !== 'undefined' && config.VUE_APP_API_SCOPE) ||
  process.env.VUE_APP_API_SCOPE

// Dieselbe Bypass-Logik wie in App.vue#created — hier nur zum Verhindern der MSAL-Init.
function isDevBypassActive() {
  const host = window.location.hostname
  const isLocalhost = host === 'localhost' || host === '127.0.0.1' || host === '[::1]'
  if (!isLocalhost) return false
  const bypassFlag = typeof config !== 'undefined' ? config.VUE_APP_DEV_MSAL_BYPASS : undefined
  const bypassOff = bypassFlag === false || bypassFlag === 'false'
  const isDevServe = process.env.NODE_ENV !== 'production'
  return (
    sessionStorage.getItem('DEV_MSAL_BYPASS') === '1' ||
    bypassFlag === true || bypassFlag === 'true' ||
    (isDevServe && !bypassOff)
  )
}

/**
 * Merkt die aktuell geoeffnete Seite, damit nach einem Entra-Redirect wieder
 * genau dorthin zurueckgekehrt wird (siehe consumePostLoginRedirect).
 *
 * Gilt fuer beide Faelle, in denen wir die Seite verlassen: die erste Anmeldung
 * (signIn) und die erzwungene Neuanmeldung nach Ablauf der Sitzung
 * (acquireTokenRedirect). Ohne den zweiten Fall landet ein Nutzer, dessen
 * Sitzung mitten in der Arbeit ablaeuft, anschliessend auf der Startseite
 * statt auf der Seite, die er gerade offen hatte.
 */
function rememberCurrentLocation() {
  const target = window.location.pathname + window.location.search
  if (target && target !== '/') {
    sessionStorage.setItem('postLoginRedirect', target)
  }
}

function applyAccount(account) {
  if (account) {
    msalState.isAuthenticated = true
    msalState.user = { userName: account.username, name: account.name }
    msalState.data = { user: { name: account.name } }
  } else {
    msalState.isAuthenticated = false
    msalState.user = null
    msalState.data = { user: null }
  }
}

// Das an $msal/msal gebundene Objekt. Getter delegieren an den reaktiven State,
// damit Templates/Computed reaktiv bleiben.
export const msalInstance = {
  get isAuthenticated() {
    return msalState.isAuthenticated
  },
  get user() {
    return msalState.user
  },
  get data() {
    return msalState.data
  },
  async signIn() {
    if (!msalApp) {
      console.warn('[msal] signIn() ignored — MSAL not initialised (dev bypass?)')
      return
    }
    // Deeplinks: Kunden rufen gespeicherte Links wie /seller/model/0246?supplierId=…
    // direkt auf. Vor dem Redirect merken wir uns das Ziel und stellen es nach
    // erfolgreicher Anmeldung wieder her (siehe consumePostLoginRedirect).
    // Bewusst nicht ueber navigateToLoginRequestUrl: dessen Zusatz-Navigation ist
    // schwerer nachvollziehbar und war hier schon einmal Ursache eines Redirect-Problems.
    rememberCurrentLocation()
    await msalApp.loginRedirect({ ...loginRequest, scopes: [...loginRequest.scopes, apiScope()].filter(Boolean) })
  },
  async signOut() {
    if (!msalApp) {
      console.warn('[msal] signOut() ignored — MSAL not initialised (dev bypass?)')
      return
    }
    await msalApp.logoutRedirect()
  },
  // Zugriff auf die rohe PublicClientApplication-Instanz, falls später benötigt.
  get instance() {
    return msalApp
  },
  /**
   * Liefert ein Access-Token für die eigene API. Wird vom axios-Interceptor bei
   * jedem Request aufgerufen; MSAL cacht die Tokens selbst und erneuert sie
   * still über den Refresh-Token, es entsteht also kein Netzwerk-Roundtrip pro
   * Request.
   *
   * Gibt `null` zurück, wenn keine Sitzung besteht (z.B. Dev-Bypass) — der
   * Interceptor sendet dann keinen Authorization-Header, statt einen kaputten.
   */
  async acquireApiToken() {
    const scope = apiScope()
    if (!msalApp || !scope) return null
    const account = msalApp.getActiveAccount() || msalApp.getAllAccounts()[0]
    if (!account) return null
    try {
      const result = await msalApp.acquireTokenSilent({ scopes: [scope], account })
      return result?.accessToken || null
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        // Refresh-Token abgelaufen o.ae. — nur dann ist ein Redirect noetig.
        // Die aktuelle Seite merken, damit der Nutzer danach dort weiterarbeitet,
        // wo er war, und nicht auf der Startseite landet.
        rememberCurrentLocation()
        await msalApp.acquireTokenRedirect({ scopes: [scope], account })
        return null
      }
      console.error('[msal] acquireTokenSilent fehlgeschlagen:', e)
      return null
    }
  }
}

/**
 * Konfiguriert MSAL und registriert `$msal`/`msal` als globale Properties.
 * Async, da `@azure/msal-browser` v3+ ein asynchrones `initialize()` verlangt.
 *
 * @param {import('vue').App} app
 */
export default async function configureMsal(app) {
  // Global-Properties in JEDEM Fall setzen (auch im Bypass), damit
  // `this.$msal?.data?.user?.name` etc. nie auf undefined läuft.
  app.config.globalProperties.$msal = msalInstance
  app.config.globalProperties.msal = msalInstance

  if (isDevBypassActive()) {
    console.log('[msal] dev bypass active — skipping @azure/msal-browser init')
    return
  }

  const domain = window.location.origin
  const clientId = (typeof config !== 'undefined' && config.VUE_APP_MSAD_CLIENT_ID) || process.env.VUE_APP_MSAD_CLIENT_ID
  const authority = (typeof config !== 'undefined' && config.VUE_APP_MSAD_AUTHORITY) || process.env.VUE_APP_MSAD_AUTHORITY

  msalApp = new PublicClientApplication({
    auth: {
      clientId,
      authority,
      redirectUri: domain,
      postLogoutRedirectUri: domain,
      // Die Rueckkehr zum Deeplink uebernehmen wir selbst (consumePostLoginRedirect),
      // damit sie nachvollziehbar bleibt und nicht von MSALs Zusatz-Navigation abhaengt.
      navigateToLoginRequestUrl: false
    },
    cache: {
      cacheLocation: 'sessionStorage'
    },
    system: {
      allowRedirectInIframe: false
    }
  })

  // msal-browser v3+: initialize() ist verpflichtend und asynchron.
  await msalApp.initialize()

  try {
    // Ergebnis eines evtl. laufenden Redirect-Logins verarbeiten.
    const redirectResult = await msalApp.handleRedirectPromise()
    if (redirectResult && redirectResult.account) {
      msalApp.setActiveAccount(redirectResult.account)
    }
  } catch (e) {
    console.error('[msal] handleRedirectPromise failed:', e)
  }

  const accounts = msalApp.getAllAccounts()
  const active = msalApp.getActiveAccount() || (accounts.length ? accounts[0] : null)
  if (active) {
    msalApp.setActiveAccount(active)
  }
  applyAccount(active)
}

/**
 * Gibt das vor dem Login gemerkte Deeplink-Ziel zurück und löscht den Marker.
 * `null`, wenn kein Ziel gespeichert war (normaler Login über die Startseite).
 */
export const consumePostLoginRedirect = () => {
  const target = sessionStorage.getItem('postLoginRedirect')
  if (target) sessionStorage.removeItem('postLoginRedirect')
  return target
}

// Rückwärtskompatible Named Exports (früher aus vue-msal). `msalMixin` ist jetzt ein
// No-op-Options-Objekt: Der `msal`-Zugriff läuft über die globale Property, ein Mixin
// wird nicht mehr benötigt. Wird von main.js/App.vue nach der Migration nicht mehr
// verwendet, bleibt aber als sicherer Fallback exportiert.
export const msalMixin = {}
